import { useDraggable } from '@dnd-kit/core';
import type { JobFileEntry } from '@jobos/shared';

import { SortableTableHeader } from '@/components/shared/SortableTableHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { byKey, useClientTableSort, type Comparator } from '@/hooks/useClientTableSort';
import { formatDate } from '@/lib/utils';
import { JobFileActionsMenu, type JobFileActions } from './JobFileActionsMenu';
import { JobFileAttachmentBadge } from './JobFileAttachmentBadge';
import { formatJobFileSize, jobFileSortSize } from './jobFileDisplay';
import { JobFileThumbnail } from './JobFileThumbnail';

// The dense alternative to the grid: a document is a name and a date, not a
// picture, and a list shows far more of them at once.

type JobFileSortField = 'name' | 'size' | 'date';

// Module scope so the memoized sort isn't invalidated on every render.
const COMPARATORS: Record<JobFileSortField, Comparator<JobFileEntry>> = {
  name: byKey((file) => file.fileName),
  // byKey pins null last in both directions, which is what mirrors want: an
  // entry with no size is not the smallest file, it is an unknown one.
  size: byKey(jobFileSortSize, { numeric: true }),
  date: byKey((file) => file.createdAt),
};

const INITIAL_SORT = { field: 'date', direction: 'desc' } as const;

interface JobFilesListProps extends Omit<JobFileActions, 'onView'> {
  jobId: string;
  files: JobFileEntry[];
  /**
   * @param ordered The set as this view shows it — sorted, not the order the
   * props arrived in, so the viewer pages the way the rows read.
   */
  onView?: (file: JobFileEntry, ordered: JobFileEntry[]) => void;
  /** The ticked files. Omit — along with `onToggleSelect` — for a list with no selection. */
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
}

/**
 * One file.
 *
 * Below `sm` the row stops being a table row and becomes a flex line: the name
 * cell takes the width, the rest of the columns fold into the secondary line
 * underneath it, and the actions button stays on the right. A phone gets
 * stacked rows rather than a table it has to drag sideways.
 */
function JobFileRow({
  jobId,
  file,
  selected,
  onToggleSelect,
  onView,
  onEdit,
  onMove,
  onDelete,
}: JobFileActions & {
  jobId: string;
  file: JobFileEntry;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const size = formatJobFileSize(file);
  const date = formatDate(file.createdAt);
  // Listeners without dnd-kit's `attributes`, for the reason JobFileCard gives:
  // a row full of buttons must not itself claim to be one.
  const { setNodeRef, listeners, isDragging } = useDraggable({ id: file.id });

  return (
    <TableRow
      ref={setNodeRef}
      {...listeners}
      // touch-none, or a press scrolls the page instead of picking the row up.
      className={`flex touch-none items-center gap-2 sm:table-row ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      {onToggleSelect && (
        <TableCell
          className="w-10 shrink-0 py-2"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected ?? false}
            onChange={() => onToggleSelect(file.id)}
            aria-label={`Select ${file.fileName}`}
            className="h-4 w-4 accent-blue-600"
          />
        </TableCell>
      )}

      <TableCell className="min-w-0 flex-1 py-2">
        <button
          type="button"
          onClick={() => onView?.(file)}
          aria-label={`View ${file.fileName}`}
          className="flex w-full min-w-0 items-center gap-2 text-left"
        >
          <span className="h-9 w-9 shrink-0 overflow-hidden rounded border border-gray-200">
            <JobFileThumbnail jobId={jobId} file={file} iconClassName="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span
              data-testid="job-file-name"
              title={file.fileName}
              className="block truncate text-sm font-medium text-gray-900"
            >
              {file.fileName}
            </span>
            {/* The folded-away columns, shown only where they have nowhere else
                to go. */}
            <span className="mt-0.5 block truncate text-xs text-gray-500 sm:hidden">
              {size} · {date}
            </span>
            <JobFileAttachmentBadge jobId={jobId} file={file} />
          </span>
        </button>
      </TableCell>

      <TableCell
        data-testid="job-file-size"
        className="hidden whitespace-nowrap py-2 text-right text-sm text-gray-600 sm:table-cell"
      >
        {size}
      </TableCell>
      <TableCell
        data-testid="job-file-uploader"
        className="hidden max-w-[10rem] truncate py-2 text-sm text-gray-600 sm:table-cell"
      >
        {file.uploadedByName ?? '—'}
      </TableCell>
      <TableCell className="hidden whitespace-nowrap py-2 text-sm text-gray-600 sm:table-cell">
        {date}
      </TableCell>

      <TableCell className="w-12 shrink-0 py-2 text-right">
        <JobFileActionsMenu
          jobId={jobId}
          file={file}
          onView={onView}
          onEdit={onEdit}
          onMove={onMove}
          onDelete={onDelete}
          className="h-8 w-8"
        />
      </TableCell>
    </TableRow>
  );
}

export function JobFilesList({
  jobId,
  files,
  selectedIds,
  onToggleSelect,
  onView,
  onEdit,
  onMove,
  onDelete,
}: JobFilesListProps) {
  const { sorted, sortField, sortDirection, handleSort } = useClientTableSort<
    JobFileEntry,
    JobFileSortField
  >(files, COMPARATORS, INITIAL_SORT);

  const onSort = (field: string) => handleSort(field as JobFileSortField);

  return (
    <div className="rounded border border-gray-200">
      <Table>
        {/* No header to sort by on a phone — the stacked rows carry their own
            labels, and the sort controls would cost a row of width each. */}
        <TableHeader className="hidden sm:table-header-group">
          <TableRow>
            {onToggleSelect && <TableHead className="w-10" />}
            <SortableTableHeader
              field="name"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Name
            </SortableTableHeader>
            <SortableTableHeader
              field="size"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
              className="text-right"
              align="right"
            >
              Size
            </SortableTableHeader>
            <TableHead>Uploaded by</TableHead>
            <SortableTableHeader
              field="date"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Date
            </SortableTableHeader>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((file) => (
            <JobFileRow
              key={file.id}
              jobId={jobId}
              file={file}
              selected={selectedIds?.has(file.id) ?? false}
              {...(onToggleSelect ? { onToggleSelect } : {})}
              onView={onView && ((viewed) => onView(viewed, sorted))}
              onEdit={onEdit}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
